// MediaRecorder as a hook (TX-US-0028): start, stop, and a finished take handed to the caller.
//
// A take is { blob, contentType, durationSeconds }, in the container the browser produced. While
// recording, the elapsed time is state - the screen renders it - and the level is written straight
// to a CSS variable on the element the caller names: it changes sixty times a second, and that is
// the stylesheet's business, not a render's.
//
// The screen is kept awake where the browser has a wake lock; a phone that sleeps mid-sentence
// stops the microphone. A recording stops itself at MAX_RECORDING_MS.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AUDIO_BITS_PER_SECOND,
  MAX_RECORDING_MS,
  baseType,
  canRecord,
  durationSeconds,
  levelOf,
  pickContainer,
  refusalMessage,
} from './recorder.js';

const TICK_MS = 250;

export default function useRecorder({ onTake, levelTarget }) {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState(null);
  // Everything a live recording holds, so stopping and unmounting release the same things.
  const live = useRef(null);
  const deliver = useRef(onTake);
  deliver.current = onTake;

  const supported = canRecord(window);

  const release = useCallback(() => {
    const held = live.current;
    if (!held) return;
    live.current = null;
    clearInterval(held.ticker);
    clearTimeout(held.limit);
    cancelAnimationFrame(held.frame);
    held.stream.getTracks().forEach((track) => track.stop());
    held.audio?.close();
    held.wakeLock?.release();
    levelTarget.current?.style.setProperty('--level', '0');
  }, [levelTarget]);

  const stop = useCallback(() => {
    const held = live.current;
    if (held && held.recorder.state !== 'inactive') held.recorder.stop();
  }, []);

  const start = useCallback(async () => {
    if (live.current) return;
    setError(null);

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (err) {
      // The one place the device answers back: permission refused, or no microphone at all.
      setError(refusalMessage(err));
      return;
    }

    const container = pickContainer((type) => MediaRecorder.isTypeSupported(type));
    const recorder = new MediaRecorder(stream, {
      ...(container ? { mimeType: container } : {}),
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    });
    const chunks = [];
    const startedAt = performance.now();

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    });
    recorder.addEventListener('stop', () => {
      const elapsed = performance.now() - startedAt;
      const contentType = baseType(recorder.mimeType || container || '');
      release();
      setRecording(false);
      navigator.vibrate?.(30);
      deliver.current({
        blob: new Blob(chunks, { type: contentType }),
        contentType,
        durationSeconds: durationSeconds(elapsed),
      });
    });

    // The level, read off the same stream the recorder hears.
    const audio = new AudioContext();
    const analyser = audio.createAnalyser();
    analyser.fftSize = 256;
    audio.createMediaStreamSource(stream).connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);

    const held = { recorder, stream, audio, wakeLock: null, ticker: 0, limit: 0, frame: 0 };
    live.current = held;

    const meter = () => {
      analyser.getByteTimeDomainData(samples);
      levelTarget.current?.style.setProperty('--level', levelOf(samples).toFixed(3));
      held.frame = requestAnimationFrame(meter);
    };
    held.frame = requestAnimationFrame(meter);
    held.ticker = setInterval(() => setElapsedMs(performance.now() - startedAt), TICK_MS);
    held.limit = setTimeout(stop, MAX_RECORDING_MS);

    // Refused when the page is not visible or the battery saver says no; recording goes on
    // either way, the screen just may dim.
    navigator.wakeLock?.request('screen').then(
      (lock) => {
        if (live.current === held) held.wakeLock = lock;
        else lock.release();
      },
      () => {},
    );

    // A slice a second, so a tab killed mid-recording has lost a second and not the take.
    recorder.start(1000);
    setElapsedMs(0);
    setRecording(true);
    navigator.vibrate?.(30);
  }, [levelTarget, release, stop]);

  // Leaving the screen ends the recording: the microphone must not outlive what asked for it.
  useEffect(() => () => {
    const held = live.current;
    if (!held) return;
    if (held.recorder.state !== 'inactive') held.recorder.stop();
    release();
  }, [release]);

  return { supported, recording, elapsedMs, error, start, stop };
}
